import { z } from 'zod';
import { SEMVER_RE, splitLegacyVersionedName } from './semver.js';

/**
 * CAD (STEP / IGES) import report — what the analysis-service's `/cad-import/parse`
 * returns next to the LLM JSON it built, what `/systems/import/preview` relays
 * to the import modal, and what `/systems/import` persists under
 * `optical_systems.provenance.cad_import`.
 *
 * Producer is Python (`analysis-service/models/cad_import.py`); this is the
 * consumer-side mirror. Keep the two in lockstep — a field missing here is
 * stripped by `fetchParsed` on the way into the modal.
 */

export const CAD_LIGHT_DIRECTIONS = ['plus_z', 'minus_z'] as const;
export type CadLightDirection = typeof CAD_LIGHT_DIRECTIONS[number];

export const CadRefusalSchema = z.object({
  code: z.enum([
    'tessellated_geometry',
    'fold_detected',
    'no_optical_faces',
    /** A face the fitter could not turn into a surface, on either engine (a
     *  freeform is otherwise fitted as ZERNIKE_SAG for both): too few cast rays
     *  reached it, its whole-trim base conic is not real out to the clip's
     *  corner, or the beam strikes a mirror off its own axis of revolution. */
    'non_symmetric_face',
    'units_unreadable',
    'open_shell',
    'fit_residual_exceeds_tolerance',
    'spacing_uncertainty_excessive',
    'misalignment_unrepresentable_on_engine',
    'unreadable_step',
    /** OpenCascade read the file but could not evaluate a face or body; `subject` names it. */
    'geometry_error',
    /** The isolated parse exceeded its wall-clock budget. */
    'parse_timeout',
    /** The isolated parse exceeded its memory budget (or was OOM-killed). */
    'parse_resource_exhausted',
    /** The file carries more faces than the importer will measure. */
    'too_many_faces',
    /** The importer itself failed — an exception outside its geometry guards,
     *  or a child process that exited outside its contract — so the file was
     *  not judged; `message` says what happened. A fault in the service. */
    'importer_error',
    /** A `reflective` window whose supplied line of sight starts on the glass
     *  side of the face it would reflect from: the face's outward normal at the
     *  strike point points away from the line's origin, so the beam would have
     *  to pass through the glass to reach it. Decided by geometry alone, never
     *  by face names; `subject` is the face, `message` names the strike point. */
    'wrong_window_side',
  ]),
  subject: z.string(),
  message: z.string(),
});
export type CadRefusal = z.infer<typeof CadRefusalSchema>;

export const CadFitReportSchema = z.object({
  method: z.enum(['exact_plane', 'exact_sphere', 'lsq_conic', 'lsq_even_asphere', 'lsq_zernike_sag']),
  cad_face_ref: z.string(),
  rms_um: z.number(),
  pv_um: z.number(),
  n_samples: z.number().int(),
  terms: z.array(z.number().int()),
  /** `'best_available'` exactly when the term ladder ran out — no rung met the
   *  tolerance AND lowered the BIC. Not the residual's verdict: that is
   *  `fit_tolerance_met`. */
  fit_quality: z.enum(['exact', 'tolerance_met', 'best_available']),
  /** Whether `rms_um` is within the fit tolerance; true for every `'exact'`
   *  surface. Optional: reports persisted before 2026-10-01 carry no key. */
  fit_tolerance_met: z.boolean().optional(),
  /** Geometric re-sample of the B-rep against the emitted surface's sag; null until computed. */
  verify_max_dev_um: z.number().nullable(),
  /** Radius sign already applied for the chosen light direction. */
  radius_mm: z.number().nullable(),
  /** Window face fitted over the request's `fit_region` only: the radius of the
   *  disc about the strike point the sag was sampled, fitted and verified over
   *  (`rms_um`, `pv_um`, `verify_max_dev_um` are measured inside it; it is the
   *  Zernike norm radius). Null when the whole trim or inscribed disc was fitted.
   *  Optional: reports persisted before 2026-10-01 carry no key. */
  fit_region_radius_mm: z.number().nullable().optional(),
  /** lsq_zernike_sag only: the Zernike numbering of the emitted surface's `terms`
   *  (OpticStudio Zernike Standard Sag, Noll); null otherwise. */
  zernike_ordering: z.enum(['standard']).nullable(),
  /** lsq_zernike_sag only: ρ = r / norm_radius_mm for the Zernike terms; null otherwise. */
  norm_radius_mm: z.number().nullable(),
  /** lsq_zernike_sag only: how many Zernike terms were emitted (sparse count); null otherwise. */
  n_zernike_terms: z.number().int().nullable(),
  /** Window face emitted with the ZERNIKE_SAG `extrapolate` cell false: max
   *  over the azimuth of |Σ A_j Z_j(1, φ)| in mm — the sag discontinuity at the
   *  edge of the Zernike disc, where the terms stop being evaluated. On a
   *  whole-trim fit that circle passes through the clip rectangle's corners and
   *  otherwise lies outside it; on a `fit_region` fit it lies INSIDE the clip,
   *  and a ray landing beyond the disc meets a step of this size. Null on every
   *  other surface. Optional: reports persisted before 2026-10-02 carry no key. */
  edge_step_mm: z.number().nullable().optional(),
});
export type CadFitReport = z.infer<typeof CadFitReportSchema>;

/** What an imported body is, optically. `'mirror'` is decided by the law of
 *  reflection at the point where two recovered axes meet — never by the part's
 *  name — and such an element carries exactly one surface (the face the beam
 *  strikes) and no glass.
 *
 *  `'window'` is a thin shell with two large opposed faces, neither rimmed by a
 *  circle, many times as wide as it is thick — a windshield, a canopy, a
 *  combiner — placed on a `reference_axis` the CALLER supplied, because such a
 *  body has no axis of revolution about the line it is used on and cannot be
 *  imported without one. It names the SHAPE the CAD carried; whether the beam
 *  goes through it or bounces off it is the caller's declared `window_use`, and
 *  shows up as the glass (a mirror solve reflects) and the surface count (one
 *  face reflecting, two transmitting). */
export const CAD_ELEMENT_KINDS = ['lens', 'mirror', 'window'] as const;
export type CadElementKind = typeof CAD_ELEMENT_KINDS[number];

/** What a supplied reference axis is for: the primary reflection off the first
 *  face, the second-surface (ghost) reflection off the far face, both
 *  reflections in one beam-split system, or the beam passing through. The same
 *  windshield does all of these at once in a real car, so nothing in the
 *  geometry decides this. */
export const CAD_WINDOW_USES = ['reflective', 'transmissive', 'ghost', 'both'] as const;
export type CadWindowUse = typeof CAD_WINDOW_USES[number];

/** Whether the emitted optical axis was recovered from the geometry's own
 *  rotational symmetry or handed over by the caller. */
export const CAD_AXIS_SOURCES = ['fitted', 'supplied'] as const;
export type CadAxisSource = typeof CAD_AXIS_SOURCES[number];

/** Which evidence produced an element's `glass_hint`.
 *
 *  `'material'` — the STEP file's own XCAF material attribute names a glass the
 *  catalog carries. That is the author's explicit assertion about what the part
 *  is MADE OF.
 *
 *  `'part_name'` — a catalog name is spelled by a run of tokens of the CAD part
 *  name. That is a lead: a part called `L2_SF2` may be following a drawing
 *  convention, a legacy spelling, or the glass it was quoted in.
 *
 *  A declared DENSITY is deliberately not a source. Measured over the 2 835
 *  indexed glasses that carry one, the median glass shares its density with
 *  nine others at EXACT equality (and with 21 others within 0.01 g/cm³), so no
 *  tolerance lets a density name a glass. */
export const CAD_GLASS_HINT_SOURCES = ['material', 'part_name'] as const;
export type CadGlassHintSource = typeof CAD_GLASS_HINT_SOURCES[number];

/**
 * The placement-invariant identity of one body's shape (analysis-service
 * services/cad/occ.py `Fingerprint`; spec 2026-10-16 §5.2). A library match
 * compares volume, area, the principal moments and the two face counts;
 * `bbox_extents_mm` is world-frame and informative only.
 */
export const CadFingerprintSchema = z.object({
  basis: z.enum(['volume', 'surface']),
  volume_mm3: z.number(),
  area_mm2: z.number(),
  /** Principal moments of inertia about the centroid per unit of the basis measure, ascending. */
  principal_moments_mm2: z.tuple([z.number(), z.number(), z.number()]),
  face_count: z.number().int(),
  cylindrical_face_count: z.number().int(),
  /** World-frame box extents, descending. Not placement-invariant; excluded from matching. */
  bbox_extents_mm: z.tuple([z.number(), z.number(), z.number()]),
});
export type CadFingerprint = z.infer<typeof CadFingerprintSchema>;

/** The STEP file's own material attribute on a body, as XCAF hands it over: a hint for the material library, never a property set. */
export const CadDeclaredMaterialSchema = z.object({
  name: z.string().nullable(),
  description: z.string().nullable(),
  density_value: z.number().nullable(),
  density_unit_name: z.string().nullable(),
});
export type CadDeclaredMaterial = z.infer<typeof CadDeclaredMaterialSchema>;

export const CadElementReportSchema = z.object({
  element_id: z.string(),
  name: z.string(),
  occurrence_path: z.string(),
  kind: z.enum(CAD_ELEMENT_KINDS),
  /** 1-based surface numbers in the emitted system, in light order. */
  surfaces: z.array(z.number().int()),
  confidence: z.number(),
  score_breakdown: z.record(z.string(), z.number()),
  /** A catalog glass name the CAD offers for this element; never sets the
   *  glass. `glass_hint_source` says what kind of claim it is. */
  glass_hint: z.string().nullable(),
  /** Which evidence named it (see CAD_GLASS_HINT_SOURCES), or null when nothing
   *  did. Null exactly when `glass_hint` is null. */
  glass_hint_source: z.enum(CAD_GLASS_HINT_SOURCES).nullable(),
  cemented_to: z.string().nullable(),
  /** Angle between the supplied reference axis and this window's first face's
   *  own surface normal, measured where that axis meets the face. Null on every
   *  element but a `'window'`: an element on a FITTED axis is at normal
   *  incidence by construction, because the axis was recovered from its own
   *  symmetry. */
  incidence_angle_deg: z.number().nullable(),
  /** Angle between the two faces' surface normals, measured at the two vertices
   *  — the wedge a windshield is deliberately built with. Null unless this is a
   *  `'window'` used transmissively or for its ghost: a reflective one emits only the face the
   *  beam strikes, so its far face's orientation is not part of the
   *  prescription. */
  wedge_angle_mrad: z.number().nullable(),
  center_thickness_mm: z.number(),
  diameter_mm: z.number(),
  proposed_clear_semi_diameter_mm: z.number(),
  /** Of the element's first body (a cemented element joins several). Optional for the same reason as CadNonElementSolidSchema's. */
  fingerprint: CadFingerprintSchema.optional(),
});
export type CadElementReport = z.infer<typeof CadElementReportSchema>;

export const CadAxisDeviationSchema = z.object({
  element_id: z.string(),
  lateral_offset_mm: z.number(),
  angle_mrad: z.number(),
  classification: z.enum(['noise', 'intentional', 'fold_or_misclassified']),
});

export const CadMechanicalConstraintSchema = z.object({
  type: z.enum(['max_element_diameter', 'fixed_spacing', 'spacing_range', 'max_total_track', 'stop_location']),
  element_id: z.string().nullable(),
  surface: z.number().int().nullable(),
  value_mm: z.number().nullable(),
  min_mm: z.number().nullable(),
  max_mm: z.number().nullable(),
  source: z.string(),
  confidence: z.number(),
});
export type CadMechanicalConstraint = z.infer<typeof CadMechanicalConstraintSchema>;

export const CadUnclassifiedSolidSchema = z.object({
  name: z.string(),
  occurrence_path: z.string(),
  is_solid: z.boolean(),
  bbox_mm: z.array(z.number()).length(6),
});

/** Why a body of the source file carries no optical surface in the emitted
 *  prescription. `'mount'` never scored as an element and is the set the
 *  bore/seat/track constraints were read from; `'image_plane'` is the unpaired
 *  planar shell placed as the image surface; `'unused_optical_candidate'`
 *  scored as an element but no surface was emitted for it, so it is neither
 *  optics nor reliably hardware. */
export const CAD_NON_ELEMENT_ROLES = ['mount', 'image_plane', 'unused_optical_candidate'] as const;
export type CadNonElementRole = typeof CAD_NON_ELEMENT_ROLES[number];


export const CadNonElementSolidSchema = z.object({
  name: z.string(),
  occurrence_path: z.string(),
  is_solid: z.boolean(),
  bbox_mm: z.array(z.number()).length(6),
  role: z.enum(CAD_NON_ELEMENT_ROLES),
  /**
   * Optional on the consumer side only: the importer always writes both (models/cad_import.py
   * requires them), but provenance saved before 2026-10-19 has neither, and a backend deployed
   * ahead of its analysis-service must still parse that service's reports. Absent reads as
   * "not measured" / "declares none".
   */
  fingerprint: CadFingerprintSchema.optional(),
  /** Null when the body declares none — the ordinary case. */
  declared_material: CadDeclaredMaterialSchema.nullable().optional(),
});
export type CadNonElementSolid = z.infer<typeof CadNonElementSolidSchema>;

export const CAD_DEFAULTABLE_FIELDS = [
  'wavelengths',
  'fields',
  'pupil',
  'stop_index',
  'glass',
  'light_direction',
  'object_distance',
  'image_distance',
  'clear_apertures',
] as const;

/** `'reference_axis_origin'` — a window import's stop is a dummy plane at the
 *  supplied reference axis's origin (for a HUD, the eye), with a placeholder
 *  semi-diameter the warnings name; the combiner itself is never the stop. */
export const CAD_STOP_SOURCES = ['iris', 'smallest_clear_aperture', 'reference_axis_origin'] as const;

/** The line the caller says the beam travels, in the STEP file's own world
 *  millimetres, and what the window on it is for.
 *
 *  A LINE and a sense, not a full rigid frame: roll about it is not taken from
 *  the caller, because nothing the caller can see fixes it, and the
 *  prescription's transverse axes are completed the same way they are for a
 *  fitted axis.
 *
 *  Supplying this turns axis RECOVERY off. Every other parse fits the optical
 *  axis through the bodies' own axes of revolution, which is exactly what a
 *  windshield, canopy or combiner does not have about the line it is used on —
 *  for a head-up display that line is the driver's eye line or the sensor's
 *  boresight, a decision of the optical design that no mechanical geometry can
 *  reveal.
 *
 *  Sent in `CadImportParseRequestSchema`, and echoed back verbatim in
 *  `CadImportReportSchema.reference_axis` so the choice reaches the saved
 *  system's provenance. */
/** One world-millimetre coordinate. `z.number()` already rejects NaN and
 *  Infinity in Zod 4; the override only replaces the message, so a caller is
 *  told what a coordinate has to be rather than "expected number, received
 *  number". */
const axisCoordinate = z.number({ error: 'each coordinate must be a finite number, in the STEP file\'s own world millimetres' });

export const CadReferenceAxisSchema = z.object({
  origin: z.array(axisCoordinate).length(3),
  /** Need not be a unit vector; the sense is the direction the beam travels. */
  direction: z.array(axisCoordinate).length(3),
  /** `'reflective'` — the beam bounces off the window's first face, the HUD
   *  combiner's primary image: one surface is emitted, with a mirror solve,
   *  bracketed by a coordinate break pair, and every thickness after it is
   *  negative. `'ghost'` — the same combiner's second-surface reflection: the
   *  beam enters the first face, reflects off the far face and leaves through
   *  the first face again, so three surfaces are emitted (first face with the
   *  plate's glass, far face as a mirror, first face again), each in its own
   *  tilted frame, and the frame the beam leaves in is the `'reflective'`
   *  import's, so the two traces compare directly. `'both'` — the two
   *  reflections in ONE system: the ghost path is the primary chain and the
   *  first-face reflection is `beam_splits[0]` off that face (the face as a
   *  mirror, then the reflective import's exit break), so one ray splits at
   *  the windshield and every analysis runs per arm; the first face carries a
   *  catalog stand-in glass because a split surface's glass is a name-only
   *  multi-configuration cell. `'transmissive'` — the beam goes through it:
   *  both faces are emitted with glass between them, each in its own tilted
   *  frame so the wedge between them is carried rather than assumed away. */
  window_use: z.enum(CAD_WINDOW_USES),
}).superRefine((axis, ctx) => {
  // The one cross-field check, and it is not cosmetic: a zero vector names no
  // line, and the importer must refuse it rather than quietly fall back to the
  // fitted axis — a fitted axis is exactly what a window has none of. Mirrors
  // `CadReferenceAxis._direction_is_a_direction` in
  // analysis-service/models/cad_import.py and the `ValueError` in
  // `services/cad/window.reference_frame`; the coordinate type above carries
  // the finiteness half of the same rule.
  if (axis.direction.every((component) => component === 0)) {
    ctx.addIssue({
      code: 'custom',
      path: ['direction'],
      message: 'direction must not be all zeros — a zero-length vector names no line for the beam to travel along',
    });
  }
});
export type CadReferenceAxis = z.infer<typeof CadReferenceAxisSchema>;

/** The beam's footprint on a window: a disc about the point the
 *  `reference_axis` strikes the glass, inside which the face is fitted. Take
 *  the radius from the HUD's own footprint on the glass (eye box plus field
 *  angle times eye-to-glass distance), with margin; a Zernike series
 *  normalised to the whole trim spends its terms on glass the beam never
 *  touches. Mirror of `CadFitRegion` in analysis-service/models/cad_import.py. */
export const CadFitRegionSchema = z.object({
  /** Radius of the disc, in the file's own millimetres; positive and finite. */
  radius_mm: z.number().positive(),
});
export type CadFitRegion = z.infer<typeof CadFitRegionSchema>;

/** Which reader the uploaded file went to, from its extension. The extension
 *  table is `CAD_IMPORT_EXTENSIONS` in src/server/constants.ts (Python:
 *  `services.cad.load.CAD_EXTENSIONS`). */
export const CAD_SOURCE_FORMATS = ['step', 'iges'] as const;
export type CadSourceFormat = typeof CAD_SOURCE_FORMATS[number];

// --- /cad-import/defeature ---------------------------------------------------------
// Recipe `defeature` (analysis-service/services/cad/defeature.py): the
// geometry derivative an optomech run meshes instead of the upload, and the
// per-body metrics that are the user-facing proof nothing structural went
// (spec 2026-10-19 §4.5). Mirror of CadDefeaturingReport in models/cad_import.py.

export const CAD_DEFEATURED_BODY_STATUSES = ['defeatured', 'raw', 'element', 'skipped'] as const;
export type CadDefeaturedBodyStatus = typeof CAD_DEFEATURED_BODY_STATUSES[number];

export const CAD_DEFEATURING_STATUSES = ['written', 'not_written'] as const;
export type CadDefeaturingStatus = typeof CAD_DEFEATURING_STATUSES[number];

/** The recipe's parameters, echoed so the record says what was applied. Provisional until gate D1 pins them (spec §11 Q4). */
export const CadDefeatureParamsSchema = z.object({
  /** A candidate face's area over the body's total area. */
  small_face_fraction: z.number(),
  /** |ΔV|/V above which a body stays as uploaded. */
  volume_bound_fraction: z.number(),
  /** Most host faces a cluster may be recessed in. */
  max_host_faces: z.number().int(),
  /** Surface types a host face may have, as GeomAbs_SurfaceType names. */
  host_surface_types: z.array(z.string()),
});
export type CadDefeatureParams = z.infer<typeof CadDefeatureParamsSchema>;

/** One cluster of faces the recipe removed. */
export const CadDefeaturedFeatureSchema = z.object({
  faces: z.number().int(),
  area_mm2: z.number(),
  /** Material the fill added, i.e. the recess's volume. */
  volume_mm3: z.number(),
});
export type CadDefeaturedFeature = z.infer<typeof CadDefeaturedFeatureSchema>;

/** One cluster of small faces the recipe found and kept, with why. */
export const CadKeptFeatureSchema = z.object({
  faces: z.number().int(),
  area_mm2: z.number(),
  reason: z.string(),
});
export type CadKeptFeature = z.infer<typeof CadKeptFeatureSchema>;

export const CadDefeaturedBodySchema = z.object({
  occurrence_path: z.string(),
  /** 'defeatured' — features removed and the body written to the derivative; 'raw' — nothing to remove, or refused (the reason says which), the body is as uploaded; 'element' — an optical element, never touched; 'skipped' — not a solid. */
  status: z.enum(CAD_DEFEATURED_BODY_STATUSES),
  /** Why the body is not 'defeatured'; null when it is. */
  reason: z.string().nullable(),
  faces_before: z.number().int(),
  faces_after: z.number().int(),
  area_removed_mm2: z.number(),
  volume_before_mm3: z.number(),
  volume_after_mm3: z.number(),
  /** (after - before) / before; positive when recesses were filled. */
  volume_change_fraction: z.number(),
  features_removed: z.array(CadDefeaturedFeatureSchema),
  largest_feature_removed: CadDefeaturedFeatureSchema.nullable(),
  features_kept: z.array(CadKeptFeatureSchema),
});
export type CadDefeaturedBody = z.infer<typeof CadDefeaturedBodySchema>;

export const CAD_DEFEATURE_RECIPE = 'defeature' as const;

/** A report as the recipe writes it. Mirror of CadDefeaturingReport in models/cad_import.py (test_cad_contract_parity.py reads this object's keys). */
export const CadDefeaturingReportSchema = z.object({
  recipe: z.literal(CAD_DEFEATURE_RECIPE),
  /** `RECIPE_VERSION` in defeature.py when the report was written. */
  recipe_version: z.string().regex(SEMVER_RE),
  params: CadDefeatureParamsSchema,
  /** The OpenCascade wheel the fill ran on; the fill can change between releases. */
  ocp_version: z.string(),
  /** 'written' — the derivative file was produced and re-read with the raw file's bodies; 'not_written' — no derivative exists and `reason` says why. */
  status: z.enum(CAD_DEFEATURING_STATUSES),
  reason: z.string().nullable(),
  bodies: z.array(CadDefeaturedBodySchema),
  bodies_defeatured: z.number().int(),
  /** Across the whole file. */
  faces_before: z.number().int(),
  faces_after: z.number().int(),
  duration_ms: z.number(),
});
export type CadDefeaturingReport = z.infer<typeof CadDefeaturingReportSchema>;

/** A report as a saved system's `provenance.cad_import.defeaturing` may hold
 *  it: as written today, or as written before 2026-10-20 with the version
 *  packed into the name. Still parsed; read through `defeaturingRecipe`, never
 *  written. */
export const CadDefeaturingReportSavedSchema = z.discriminatedUnion('recipe', [
  CadDefeaturingReportSchema,
  CadDefeaturingReportSchema.omit({ recipe: true, recipe_version: true }).extend({ recipe: z.literal('defeature@1') }),
]);
export type CadDefeaturingReportSaved = z.infer<typeof CadDefeaturingReportSavedSchema>;

/** The recipe and its version a report names, whichever form the report carries. */
export function defeaturingRecipe(report: CadDefeaturingReportSaved): { recipe: string; recipe_version: string } {
  if ('recipe_version' in report) return { recipe: report.recipe, recipe_version: report.recipe_version };
  const { name, version } = splitLegacyVersionedName(report.recipe);
  return { recipe: name, recipe_version: version };
}

/** The `payload` part of a multipart POST /cad-import/defeature. */
export const CadDefeatureRequestSchema = z.object({
  filename: z.string(),
  /** Occurrence paths of the import's element bodies, one per body (a cemented element's 'a + b' path split on ' + '). Never defeatured. */
  element_occurrence_paths: z.array(z.string()),
});
export type CadDefeatureRequest = z.infer<typeof CadDefeatureRequestSchema>;

export const CadImportReportSchema = z.object({
  /** Bumped when a persisted report's meaning changes; lets a reader tell an old row apart. */
  report_version: z.literal(1),
  /** Additive: reports persisted before IGES import existed carry no key and
   *  are STEP. */
  source_format: z.enum(CAD_SOURCE_FORMATS).optional(),
  /** SHA-256 of the uploaded file, whichever format it is (the name predates IGES). */
  step_sha256: z.string(),
  /** STEP application protocol ('AP242', …); null for IGES, which has no schema. */
  step_schema: z.string().nullable(),
  declared_length_unit: z.string().nullable(),
  healed: z.boolean(),
  /** Faces `ShapeUpgrade_UnifySameDomain` merged back into one across the whole
   *  file, i.e. how many split faces the exporter wrote. 0 for a file written in
   *  one piece, which is every OpticStudio and CadQuery export. */
  faces_merged: z.number().int().nonnegative(),
  light_direction: z.enum(CAD_LIGHT_DIRECTIONS),
  /** `'heuristic'` — the larger-first-element rule chose it; `'user'` — the
   *  caller's `light_direction` was read; `'supplied_axis'` — nobody chose one:
   *  the sense is the supplied reference axis's own direction, and
   *  `light_direction` was not read at all. */
  light_direction_source: z.enum(['heuristic', 'user', 'supplied_axis']),
  /** `'fitted'` — the optical axis was recovered from the bodies' own rotational
   *  symmetry, so every element on it reads at normal incidence by construction.
   *  `'supplied'` — the caller gave the line the beam travels and no axis was
   *  fitted, which is the only way a window or combiner can be imported. */
  axis_source: z.enum(CAD_AXIS_SOURCES),
  /** The request's `reference_axis`, echoed exactly as the caller wrote it
   *  (origin, unnormalised direction, window_use) when `axis_source` is
   *  `'supplied'`; null when the axis was fitted. The line of sight is a design
   *  decision the geometry does not record, and this is the only place it is
   *  kept once the system is saved — `axis` below is the normalised line the
   *  prescription was laid out on. Optional as well as nullable: reports
   *  persisted before it existed carry no key. */
  reference_axis: CadReferenceAxisSchema.optional().nullable(),
  axis: z.object({
    origin: z.array(z.number()).length(3),
    direction: z.array(z.number()).length(3),
  }),
  elements: z.array(CadElementReportSchema),
  /** Keyed by 1-based surface number as a string. */
  surface_fits: z.record(z.string(), CadFitReportSchema),
  axis_deviations: z.array(CadAxisDeviationSchema),
  defaults_applied: z.array(z.enum(CAD_DEFAULTABLE_FIELDS)),
  /** Where the stop guess came from: a physical iris body in the CAD, or the smallest-clear-aperture rule. */
  stop_source: z.enum(CAD_STOP_SOURCES),
  unclassified: z.array(CadUnclassifiedSolidSchema),
  /** Every body of the source file the prescription does not use as an optical
   *  element, in file order — the selector a STOP consumer applies to the
   *  retained geometry that the saved system's
   *  `constraints.mechanical.retained_geometry_ref` points at. `unclassified`
   *  is the `role: 'mount'` subset of this list; the other two roles name
   *  bodies that scored as optics and so appear in neither the element list nor
   *  `unclassified`. */
  non_element_solids: z.array(CadNonElementSolidSchema),
  constraints: z.array(CadMechanicalConstraintSchema),
  has_unrepresented_misalignment: z.boolean(),
  warnings: z.array(z.string()),
  /**
   * Filled in by the backend after the import is confirmed (POST /cad-import/defeature):
   * the geometry derivative the optomech run meshes and its per-body metrics. Null on a
   * parse response and on a preview (the Python model's default, serialised), absent on
   * reports persisted before the recipe existed.
   */
  defeaturing: CadDefeaturingReportSavedSchema.nullable().optional(),
});
export type CadImportReport = z.infer<typeof CadImportReportSchema>;

/** The engine a parse can be asked for. Both Zemax engines (hosted and BYO) map
 *  onto `'zemax'` — the BYO bridge runs the same OpticStudio. The value is
 *  accepted and threaded through to the parser but decides nothing in the
 *  pipeline: both engines get the same prescription — an intentionally
 *  decentred or tilted element bracketed by a coordinate break pair, and a
 *  face that is not a surface of revolution fitted as a ZERNIKE_SAG surface
 *  (`method: 'lsq_zernike_sag'`), which the Optiland converter traces. */
export const CAD_PARSE_ENGINES = ['optiland', 'zemax'] as const;
export type CadParseEngine = typeof CAD_PARSE_ENGINES[number];

/** The `payload` part of the analysis-service `POST /cad-import/parse` call
 *  (Node → Python), which is `multipart/form-data`: the STEP or IGES bytes travel as
 *  the `file` part under the user's filename, and this object, JSON-encoded,
 *  as the `payload` part (src/lib/analysisServiceTransport.ts
 *  `fetchAnalysisMultipart`). Never base64 inside JSON — the cap is 100 MB. */
export const CadImportParseRequestSchema = z.object({
  filename: z.string(),
  /** null → the larger-first-element heuristic decides and the report says so.
   *  Not read at all when `reference_axis` is given, which states a direction of
   *  propagation itself. */
  light_direction: z.enum(CAD_LIGHT_DIRECTIONS).nullable(),
  /** Omitted → `'optiland'`. Recorded for the trace; it selects nothing in the
   *  parse (see CAD_PARSE_ENGINES). */
  engine: z.enum(CAD_PARSE_ENGINES).optional(),
  /** Omitted or null → the optical axis is fitted through the bodies' own axes
   *  of revolution, exactly as it always has been. Given → no axis is fitted,
   *  this line is used, and the one window shell in the file is placed on it. */
  reference_axis: CadReferenceAxisSchema.nullable().optional(),
  /** Only beside `reference_axis`: fit the window face over this disc about
   *  the strike point instead of its whole trim. The rectangular clip stays the
   *  whole trim; outside the disc the emitted surface is its bare base sphere
   *  (extrapolate false), with a sag step at the disc's edge of
   *  `surface_fits[].edge_step_mm`, so the disc must enclose the beam's whole
   *  footprint on the glass with margin. The report's
   *  `surface_fits[].fit_region_radius_mm` echoes it. The Python model refuses
   *  it without a `reference_axis`. */
  fit_region: CadFitRegionSchema.nullable().optional(),
});
export type CadImportParseRequest = z.infer<typeof CadImportParseRequestSchema>;

/** Python → Node. Exactly one of `llm_json` or a non-empty `refusals` is present on success=false. */
export const CadImportParseResponseSchema = z.object({
  success: z.boolean(),
  errors: z.array(z.object({ field: z.string(), message: z.string(), severity: z.string() })),
  warnings: z.array(z.object({ field: z.string(), message: z.string(), severity: z.string() })),
  llm_json: z.unknown().nullable(),
  cad_import: CadImportReportSchema.nullable(),
  refusals: z.array(CadRefusalSchema),
});
export type CadImportParseResponse = z.infer<typeof CadImportParseResponseSchema>;
